import React, { useState, useEffect } from "react";
import { shallow } from "zustand/shallow";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  Card,
  Select,
  Text,
  Divider,
  Stack,
  Grid,
  Flex,
  ActionIcon,
  Button,
  Checkbox,
  Box,
  Collapse,
  Badge,
  Loader,
  Title,
  UnstyledButton,
  SegmentedControl,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import toast from "react-hot-toast";
import {
  IconUser,
  IconTrash,
  IconSparkles,
  IconCaretRightFilled,
  IconCaretDownFilled,
} from "@tabler/icons-react";

import ReportRecipeEntityPicker from "@components/ReportRecipe/ReportRecipeRunner/ReportRecipeEntityPicker";
import useAppStore from "./report-recipe-runner-store";
import abbreviateNumber from "@util/abbreviateNumber";
import SavedReportRecipeCreate from "./SavedReportRecipeCreate";
import qbGenerateRequest from "./qb-generate-request";

import { LocationSelect } from "@components/Location";

export default function ResponseFilter({
  showEntityPicker,
  afterRunReport,
  showSavedRecipeCreate,
}) {
  const [resetting, setResetting] = useState(false);
  const managerInfo = useSelector((state) => state.manager);
  const isOrgManager = managerInfo?.organization_id ? true : false;

  const {
    locations,
    orgs,
    recipeInfo,
    orgManagerEffortSource,
    reportDataQuery,
    setReportDataQuery,
    setQueryData,
    queryData,
    setOrgManagerEffortSource,
    reset,
  } = useAppStore(
    (state) => ({
      locations: state.locations,
      queryData: state.query_data,
      orgs: state.orgs,
      recipeInfo: state.recipe_info,
      orgManagerEffortSource: state.org_manager_effort_source,
      setQueryData: state.setQueryData,
      reportDataQuery: state.report_data_query,
      setReportDataQuery: state.setReportDataQuery,
      setOrgManagerEffortSource: state.setOrgManagerEffortSource,
      reset: state.reset,
    }),
    shallow
  );

  function runReport(effortReqData) {
    const rdq = {
      query_options: 1,
      visualizer: queryData.visualizer,
      ...effortReqData,
    };

    if (locations.length) {
      rdq.location_ids = locations.map((m) => m.id);
      rdq.saved_recipe_locations = locations;
    }
    if (orgs.length) {
      rdq.organization_ids = orgs.map((m) => m.id);
      rdq.saved_recipe_orgs = orgs;
    }

    setReportDataQuery({ ...queryData });
    axios
      .post(`/report-recipes/${recipeInfo.id}/run-report/`, {
        ...rdq,
      })
      .then((res) => {
        afterRunReport();
      })
      .catch((err) => {
        toast.error(err);
      });
  }

  // useEffect(() => {
  //   setQueryData({ ...queryData, campaign_effort_id: "" });
  // }, [JSON.stringify(locations), JSON.stringify(orgs)]);

  const entitySelectionSatisfied =
    locations.length > 0 || orgs.length > 0 || isOrgManager;
  const effortId = queryData.campaign_effort_id;
  const showEffortQuestions =
    !resetting && effortId && entitySelectionSatisfied;

  return (
    <div style={{ position: "relative" }}>
      {!resetting && (
        <Stack gap="xs">
          {isOrgManager ? (
            <div>
              <OrgManagerEffortPicker />
            </div>
          ) : (
            <>
              {showEntityPicker && (
                <>
                  <ReportRecipeEntityPicker />
                </>
              )}
              {entitySelectionSatisfied && (
                <>
                  <EffortSelect
                    onChange={(obj) =>
                      setQueryData({
                        ...queryData,
                        campaign_effort_id: obj?.value || "",
                        campaign_efforts_preserved_options: obj?.label
                          ? [obj]
                          : [],
                      })
                    }
                    value={effortId}
                    reqData={{
                      report_recipe_id: recipeInfo.id,
                      location_ids: locations.map((m) => m.id),
                      organization_ids: orgs.map((m) => m.id),
                      scope: 6,
                      // scope: managerInfo?.location_id ? 7 : 6,
                    }}
                  />
                </>
              )}
            </>
          )}
          {showEffortQuestions && (
            <Box mt="lg" style={{ position: "relative" }}>
              <EffortQuestions
                reqData={{
                  report_recipe_id: recipeInfo.id,
                  location_ids: locations.map((m) => m.id),
                  organization_ids:
                    isOrgManager && locations.length === 0
                      ? [managerInfo.organization_id]
                      : orgs.map((m) => m.id),
                  effort_id: effortId,
                }}
                runReport={runReport}
                onReset={() => {
                  reset();
                  setResetting(true);
                  setTimeout(() => {
                    setResetting(false);
                  }, 1);
                }}
              >
                {showSavedRecipeCreate && (
                  <SavedReportRecipeCreate
                    recipeId={recipeInfo.id}
                    reqData={{
                      report_recipe_id: recipeInfo.id,
                      query_data: {
                        ...queryData,
                        saved_recipe_orgs: orgs,
                        saved_recipe_locations: locations,
                        organization_ids: orgs.map((m) => m.id),
                        location_ids: locations.map((m) => m.id),
                      },
                    }}
                  />
                )}
              </EffortQuestions>
            </Box>
          )}
        </Stack>
      )}
    </div>
  );
}

const EffortSelect = ({
  reqData = {},
  onChange,
  value = null,
  disabled = false,
}) => {
  const { queryData } = useAppStore(
    (state) => ({
      queryData: state.query_data,
    }),
    shallow
  );

  const { data: effortData = [], isLoading } = useQuery({
    queryKey: ["efforts", JSON.stringify(reqData)],
    queryFn: () =>
      axios
        .post("/query-components/campaign-effort-multi/", reqData)
        .then(({ data }) =>
          data.response.map((m) => ({
            label: m.display_text,
            value: m.value,
          }))
        )
        .catch((err) => {
          return [];
        }),
    enabled: Boolean(reqData.report_recipe_id) && !disabled,
  });

  const optionSource =
    queryData?.campaign_efforts_preserved_options?.length > 0
      ? queryData?.campaign_efforts_preserved_options
      : effortData;

  return (
    <Select
      searchable
      clearable
      data={optionSource || []}
      placeholder="Search efforts"
      onChange={(e, d) => {
        onChange(d);
      }}
      value={value}
      size="sm"
      label="Effort"
      disabled={disabled}
    />
  );
};

const EffortQuestions = ({ reqData, runReport, children, onReset }) => {
  const { queryData, setQueryData, recipeInfo } = useAppStore(
    (state) => ({
      queryData: state.query_data,
      setQueryData: state.setQueryData,
      recipeInfo: state.recipe_info,
    }),
    shallow
  );

  const {
    data: questions = [],
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["questions", JSON.stringify(reqData)],
    queryFn: () =>
      axios
        .post("/query-components/engagement-items-for-effort/", reqData)
        .then(({ data }) =>
          data.response.map((m) => ({
            title: m.interrogative || "Untitled Response",
            id: m.id,
            select_items: m.select_items,
          }))
        ),
    enabled: Boolean(reqData.effort_id),
  });

  useEffect(() => {
    if (!questions.length) return;
    const currentQueryData = useAppStore.getState().query_data;
    setQueryData({
      ...currentQueryData,
      selected_questions: questions.map((m) => ({
        ...m,
        selected_items:
          currentQueryData?.selected_questions?.find((q) => q.id === m.id)
            ?.selected_items || [],
      })),
    });
  }, [questions]);

  function handleChange(question, selectedItems) {
    setQueryData({
      ...queryData,
      selected_questions: queryData.selected_questions.map((m) =>
        m.id === question.id ? { ...m, selected_items: selectedItems } : m
      ),
    });
  }

  function handleApply() {
    runReport({
      ...reqData,
      ...getRequestData(),
      campaign_effort_id: reqData.effort_id,
    });
  }

  function getRequestData() {
    return {
      ...reqData,
      ...qbGenerateRequest([], queryData, recipeInfo),
      report_recipe_id: recipeInfo.id,
    };
    // const questionsToSave = queryData.selected_questions.filter(
    //   (f) => f.selected_items.length > 0
    // );
    // const uniqueQuestionCount = questionsToSave.length;
    // const formattedQuestions = queryData.selected_questions.map((m) => ({
    //   question_id: m.id,
    //   select_item_ids: m.selected_items.map((m) => m.id),
    // }));
    // const obj = {
    //   ...reqData,
    //   campaign_effort_id: reqData.effort_id,
    //   select_item_ids: formattedQuestions.flatMap((m) => m.select_item_ids),
    //   or_query: uniqueQuestionCount === 1,
    //   saved_selected_questions: questionsToSave,
    // };
    // delete obj.effort_id;
    // return obj;
  }

  // if (isLoading || isFetching)
  //   return <Loader size="sm" color="gray" type="dots" />;

  if (error)
    return (
      <Text size="sm" c="red">
        Error loading questions
      </Text>
    );

  if (!queryData.selected_questions) return null;

  const selectedQuestions = queryData.selected_questions.filter(
    (m) => m.selected_items.length > 0
  );
  const questionsWithSelectedItemsCount = selectedQuestions.length;
  const hasOneSelectedQuestion = selectedQuestions.length > 0;
  const hasOneQuestionWithMultipleSelections =
    queryData.selected_questions.some((m) => m.selected_items.length > 1);
  const loading = isLoading || isFetching;

  if (loading)
    return (
      <Box>
        <Loader size="sm" type="dots" />
      </Box>
    );

  if (questions.length === 0)
    return (
      <Text size="sm" c="yellow" fw={600}>
        No queryable questions found
      </Text>
    );

  return (
    <Grid gutter="xl" style={{ position: "relative" }}>
      <Grid.Col span={{ base: 12, md: 8 }}>
        <Stack gap="xl">
          {questions.map((question) => {
            const isIncluded = queryData.selected_questions.find(
              (m) => m.id === question.id && m.selected_items.length > 0
            );
            const disabled =
              (!isIncluded && hasOneQuestionWithMultipleSelections) ||
              (isIncluded && questionsWithSelectedItemsCount > 1);
            return (
              <QuestionAdder
                key={question.id}
                question={question}
                onChange={(e) => handleChange(question, e)}
                selectedItems={
                  queryData.selected_questions.find((m) => m.id === question.id)
                    ?.selected_items || []
                }
                disabled={disabled}
              />
            );
          })}
        </Stack>
      </Grid.Col>
      <Grid.Col
        span={{ base: 12, md: 4 }}
        style={{ position: "sticky", top: 60, alignSelf: "flex-start" }}
      >
        <Stack gap="sm">
          <EstimatedStats
            reqData={{
              ...getRequestData(),
              query_only: true,
            }}
          />
          <SummaryBox selectedQuestions={selectedQuestions} />
          <Button
            onClick={handleApply}
            disabled={!hasOneSelectedQuestion}
            size="md"
            fullWidth
            color="green"
            leftSection={<IconSparkles />}
          >
            Run Report
          </Button>
          {selectedQuestions.length > 0 && (
            <>
              {children && <Box mt="lg">{children}</Box>}
              <ClearChoicesButton
                onConfirm={() => {
                  onReset();
                  // setQueryData({
                  //   ...queryData,
                  //   selected_questions: queryData.selected_questions.map(
                  //     (m) => ({
                  //       ...m,
                  //       selected_items: [],
                  //     })
                  //   ),
                  // });
                }}
              />
            </>
          )}
        </Stack>
      </Grid.Col>
    </Grid>
  );
};

const QuestionAdder = ({ question, onChange, selectedItems, disabled }) => {
  const [opened, { toggle }] = useDisclosure(false);

  const handleButtonClick = (item) => {
    const selectedItem = selectedItems.find((f) => f.id === item.id);
    if (selectedItem) {
      onChange([...selectedItems.filter((f) => f.id !== item.id)]);
    } else {
      onChange([...selectedItems, { ...item }]);
    }
  };
  const selectedItemIds = selectedItems.map((f) => f.id);
  const disabledMessage =
    selectedItemIds.length > 0
      ? "Additional responses are disabled — selecting more will return 0 users"
      : "Selecting responses from this question is disabled because you have selected multiple responses from another question";
  const totalResponses = question.select_items.reduce(
    (acc, cur) => acc + cur.responses_total,
    0
  );

  return (
    <div>
      <Flex gap="xs" align="center">
        <ActionIcon
          size="sm"
          variant="light"
          radius="xl"
          color="gray"
          onClick={toggle}
        >
          {opened ? (
            <IconCaretDownFilled size={16} />
          ) : (
            <IconCaretRightFilled size={16} />
          )}
        </ActionIcon>
        <div>
          <Flex align="center" gap="xs">
            <Text size="lg" fw={600}>
              {question.title}
            </Text>
            {selectedItems.length > 0 && (
              <Badge size="xs" color="blue" variant="light">
                {selectedItems.length} selected
              </Badge>
            )}
          </Flex>
        </div>
      </Flex>
      <Collapse in={opened} mt="xs">
        {disabled ? (
          <Text size="xs" c="yellow" mb="xs">
            {disabledMessage}
          </Text>
        ) : (
          <Text size="xs" c="gray" mb="xs">
            Include the respondants who answered this question with the
            following options
          </Text>
        )}
        <Stack gap="xs" style={{ maxHeight: "300px", overflowY: "auto" }}>
          {question.select_items.map((item) => (
            <SelectItem
              key={item.id}
              item={item}
              onClick={handleButtonClick}
              checked={selectedItemIds.includes(item.id)}
              onChange={() => handleButtonClick(item)}
              disabled={disabled && !selectedItemIds.includes(item.id)}
            />
          ))}
        </Stack>
      </Collapse>
    </div>
  );
};

const SelectItem = ({ item, onChange, checked, disabled }) => {
  return (
    <Flex
      align="center"
      gap="xs"
      p="xs"
      style={{
        backgroundColor: checked
          ? "var(--mantine-color-blue-0)"
          : "var(--mantine-color-gray-1)",
        borderRadius: "var(--mantine-radius-sm)",
        border: "1px solid var(--mantine-color-gray-2)",
        opacity: disabled ? 0.3 : 1,
        transition: "opacity 0.2s ease-in-out",
      }}
    >
      <Checkbox
        checked={checked}
        onChange={onChange}
        size="sm"
        disabled={disabled}
      />
      <Text>{item.label}</Text>
      <Badge
        leftSection={<IconUser size={14} />}
        size="xs"
        color="gray"
        variant="light"
      >
        {item.responses_total || 0}
      </Badge>
    </Flex>
  );
};

const EstimatedStats = ({ reqData }) => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["estimated-stats", JSON.stringify(reqData)],
    queryFn: () =>
      axios
        .post(`/report-recipes/${reqData.report_recipe_id}/query-only/`, {
          ...reqData,
        })
        .then(({ data }) => data.response)
        .catch((err) => {
          return [];
        }),
    enabled: Boolean(reqData.report_recipe_id),
  });

  if (!stats) {
    return (
      <Stack gap="xs">
        <Box
          p="xs"
          style={{
            backgroundColor: "var(--mantine-color-gray-1)",
            borderRadius: "var(--mantine-radius-sm)",
            border: "1px solid var(--mantine-color-gray-2)",
          }}
        >
          <Loader size="lg" color="gray" type="dots" />
        </Box>
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      {stats.map((stat, idx) => (
        <Box
          key={idx}
          p="xs"
          style={{
            backgroundColor: "var(--mantine-color-gray-1)",
            borderRadius: "var(--mantine-radius-sm)",
            border: "1px solid var(--mantine-color-gray-2)",
          }}
        >
          <Flex align="center" gap="xs">
            <Title order={2} mb={0}>
              ~{abbreviateNumber(stat.count)}
            </Title>
            {/* {isLoading && <Loader size="xs" color="gray" type="dots" />} */}
          </Flex>
          <Text size="sm" c="gray">
            {stat.count_string} selected
          </Text>
        </Box>
      ))}
    </Stack>
  );
};

const SummaryBox = ({ selectedQuestions }) => {
  if (selectedQuestions.length === 0) return null;
  const individualSelectItems = selectedQuestions.flatMap((m) =>
    m.selected_items.map((si) => ({
      ...si,
      question_text: m.title,
      question_id: m.id,
    }))
  );
  const uniqueQuestionCount = selectedQuestions.length;
  return (
    <div>
      <Text size="sm" mb="sm">
        This report will include users who answered the following questions with
        the following options:
      </Text>
      {individualSelectItems.map((m, idx) => (
        <div key={idx}>
          <Box
            p="xs"
            style={{
              backgroundColor: "var(--mantine-color-gray-1)",
              borderRadius: "var(--mantine-radius-sm)",
              border: "1px solid var(--mantine-color-gray-2)",
            }}
          >
            <Text size="sm" fw={600}>
              {m.question_text}
            </Text>
            <Text size="sm">{m.label}</Text>
          </Box>
          {idx < individualSelectItems.length - 1 && (
            <Divider
              my="sm"
              label={uniqueQuestionCount > 1 ? "and" : "or"}
              labelPosition="left"
            />
          )}
        </div>
      ))}
    </div>
  );
};

const ClearChoicesButton = ({ onConfirm }) => {
  return (
    <UnstyledButton
      // style={{
      //   color: "var(--mantine-color-gray-5)",
      // }}
      mt="md"
      onClick={() => onConfirm()}
    >
      <Flex gap="xs" align="center">
        <IconTrash />
        <Text fw={600} size="sm">
          Clear form selections
        </Text>
      </Flex>
    </UnstyledButton>
  );
};

const OrgManagerEffortPicker = () => {
  const {
    orgManagerEffortSource,
    setOrgManagerEffortSource,
    locations,
    setLocations,
    setReportData,
    recipeInfo,
    queryData,
    setQueryData,
  } = useAppStore(
    (state) => ({
      orgManagerEffortSource: state.org_manager_effort_source,
      setOrgManagerEffortSource: state.setOrgManagerEffortSource,
      locations: state.locations,
      setLocations: state.setLocations,
      setReportData: state.setReportData,
      recipeInfo: state.recipe_info,
      queryData: state.query_data,
      setQueryData: state.setQueryData,
    }),
    shallow
  );

  const effortId = queryData.campaign_effort_id;
  const managerInfo = useSelector((state) => state.manager);
  const managerOrganizationId = managerInfo.organization_id;

  return (
    <div>
      <SegmentedControl
        value={orgManagerEffortSource}
        onChange={(e) => {
          setOrgManagerEffortSource(e);
          setLocations([]);
          setReportData(null);
          setQueryData({
            ...queryData,
            campaign_effort_id: "",
            campaign_efforts_preserved_options: [],
          });
        }}
        data={[
          { label: "My Efforts", value: "my_efforts" },
          { label: "My Locations", value: "my_locations" },
        ]}
        size="xs"
        mb="xs"
      />
      <Stack gap="xs">
        {orgManagerEffortSource === "my_efforts" && (
          <>
            <EffortSelect
              disabled={!managerOrganizationId}
              reqData={{
                report_recipe_id: recipeInfo.id,
                organization_ids: [managerOrganizationId],
                scope: 6,
              }}
              onChange={(obj) => {
                setQueryData({
                  ...queryData,
                  campaign_effort_id: obj?.value || "",
                  campaign_efforts_preserved_options: obj?.label ? [obj] : [],
                });
                setLocations([], true);
              }}
              value={effortId}
            />
            {effortId ? (
              <LocationSelect
                value={locations.length === 1 ? locations[0].id : ""}
                onChange={(e) => {
                  if (!e) {
                    setLocations([], true);
                  } else {
                    setLocations(
                      [...locations, { id: e.id, name: e.name }],
                      true
                    );
                  }
                  setReportData(null);
                }}
                objectOnChange
                label="Location (optional)"
                reqData={{
                  campaign_effort_id: effortId,
                }}
              />
            ) : (
              <Select
                label="Location (optional)"
                disabled
                placeholder="Select a location"
                data={[]}
              />
            )}
          </>
        )}
        {orgManagerEffortSource === "my_locations" && (
          <>
            <LocationSelect
              value={locations.length === 1 ? locations[0].id : ""}
              onChange={(e) => {
                if (!e) {
                  setLocations([]);
                } else {
                  setLocations([...locations, { id: e.id, name: e.name }]);
                }
                setReportData(null);
                setQueryData({
                  ...queryData,
                  campaign_effort_id: "",
                  campaign_efforts_preserved_options: [],
                });
              }}
              // disabled={
              //   locations.length === 1 || orgs.length > 0
              //     ? true
              //     : false || managerQueryEntity !== "location"
              // }
              // disabledIds={locations.map((m) => m.id)}
              objectOnChange
            />
            <EffortSelect
              disabled={locations.length === 0}
              reqData={{
                report_recipe_id: recipeInfo.id,
                location_ids: locations.map((m) => m.id),
                scope: 7,
              }}
              onChange={(obj) => {
                setQueryData({
                  ...queryData,
                  campaign_effort_id: obj?.value || "",
                  campaign_efforts_preserved_options: obj?.label ? [obj] : [],
                });
                setReportData(null);
              }}
              value={effortId || null}
            />
          </>
        )}
      </Stack>
    </div>
  );
};
